/**
 * Centralized permission model for AlloyBuild.
 *
 * Backend effective-access is authoritative. The fixture role table below is generated
 * by scripts/sync-permission-catalogue.mjs from the pinned backend contract.
 * All permission checks (route guards, action gating, navigation filtering)
 * should use the loaded-capability helpers for API actions; `hasPermission()`
 * is a generated fixture/presentation helper.
 *
 * Do not scatter ad-hoc role checks throughout the codebase.
 */

export type Permission =
  // Wallets
  | "wallet:create"
  | "wallet:read"
  | "wallet:full"
  // Transfers
  | "transfer:create"
  | "transfer:read"
  | "transfer:approve"
  | "transfer:reject"
  | "transfer:escalate"
  | "transfer:full"
  // Approvals
  | "approval:read"
  | "approval:approve"
  | "approval:reject"
  | "approval:hold"
  | "approval:escalate"
  // Admin
  | "admin:access"
  | "user:invite"
  | "user:suspend"
  | "user:read"
  | "user:full"
  | "role:read"
  | "role:full"
  | "group:read"
  | "group:full"
  | "service-account:manage"
  | "webhook:manage"
  | "audit-log:read"
  // Configuration
  | "org:configure"
  | "auth:configure"
  | "settings:read"
  | "settings:full"
  | "notifications-prefs:read"
  | "notifications-prefs:full"
  // Oversight
  | "policy:read"
  | "policy:full"
  | "monitoring:read"
  | "reconciliation:run"
  | "evidence:read"
  | "evidence:export"
  // Treasury
  | "treasury:read"
  | "treasury:create-intent"
  | "treasury:execute"
  // Developer
  | "webhook:manage"
  // Security / identity
  | "sso:manage";

/**
 * Fixture/presentation role keys. Backend grants, never names, authorize actions.
 * Both hyphen and underscore forms are accepted
 * so that URL params, fixtures, and tenant roleLabels can all resolve.
 */
export type AppRole =
  | "org-admin"
  | "security-admin"
  | "developer"
  | "wallet-admin"
  | "wallet_operator"
  | "compliance-officer"
  | "policy_approver"
  | "treasury-operator"
  | "finance_operator"
  | "finance_viewer"
  | "auditor"
  | "support-readonly"
  | "support_readonly"
  | "executive_viewer"
  | "finance_lead"
  | "governed_transfer_approver"
  | "compliance_admin"
  | "compliance_reviewer"
  | "transaction_signer"
  | "hotwallet_viewer"
  | "hotwallet_cosigner_requester"
  | "hotwallet_cosigner_approver";

// Generated fixture grants. Edit the backend contract/declarations, then regenerate; never hand-edit.
const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  "org-admin": ["wallet:read","user:invite","user:suspend","user:read","role:read","role:full","group:read","audit-log:read","admin:access"],
  "security-admin": [],
  "developer": [],
  "wallet-admin": ["wallet:create","wallet:read","wallet:full","transfer:read","approval:read","settings:read","settings:full","notifications-prefs:read","notifications-prefs:full"],
  "wallet_operator": ["wallet:create","wallet:read","wallet:full","transfer:create","transfer:read","transfer:full"],
  "compliance-officer": [],
  "policy_approver": [],
  "treasury-operator": [],
  "finance_operator": [],
  "finance_viewer": [],
  "auditor": ["wallet:read","transfer:read","approval:read","audit-log:read","settings:read","policy:read","admin:access"],
  "support-readonly": [],
  "support_readonly": [],
  "executive_viewer": ["wallet:read","transfer:read","approval:read","settings:read","notifications-prefs:read","policy:read"],
  "finance_lead": ["wallet:read","transfer:read","approval:read","settings:read","notifications-prefs:read","notifications-prefs:full"],
  "governed_transfer_approver": ["transfer:read","approval:read","approval:approve","approval:reject"],
  "compliance_admin": ["policy:read"],
  "compliance_reviewer": ["policy:read"],
  "transaction_signer": ["wallet:read"],
  "hotwallet_viewer": [],
  "hotwallet_cosigner_requester": [],
  "hotwallet_cosigner_approver": [],
};

function normalizeRole(role: string | null | undefined): AppRole | null {
  if (!role) return null;
  const key = role.trim().toLowerCase().replace(/\s+/g, "-");
  // Accept both "org admin" and "org-admin" style labels.
  const aliasMap: Record<string, AppRole> = {
    "org-admin": "org-admin",
    "org_admin": "org-admin",
    "organization-admin": "org-admin",
    // The backend's durable organization admin role (render-customer-auth-sql.py seeds it, and
    // PATCH /v1/admin/users/{id}/roles assigns it). Without this alias a real first admin whose
    // roleNames start with "organization_admin" resolved to no role at all (ALL-4856).
    organization_admin: "org-admin",
    "security-admin": "security-admin",
    security_admin: "security-admin",
    transaction_signer: "transaction_signer",
    hotwallet_viewer: "hotwallet_viewer",
    hotwallet_cosigner_requester: "hotwallet_cosigner_requester",
    hotwallet_cosigner_approver: "hotwallet_cosigner_approver",
    executive_viewer: "executive_viewer",
    finance_lead: "finance_lead",
    governed_transfer_approver: "governed_transfer_approver",
    compliance_admin: "compliance_admin",
    compliance_reviewer: "compliance_reviewer",
    developer: "developer",
    "wallet-admin": "wallet-admin",
    wallet_admin: "wallet-admin",
    "wallet-operator": "wallet_operator",
    wallet_operator: "wallet_operator",
    "compliance-officer": "compliance-officer",
    compliance_officer: "compliance-officer",
    "policy-approver": "policy_approver",
    policy_approver: "policy_approver",
    "treasury-operator": "treasury-operator",
    treasury_operator: "treasury-operator",
    "finance-operator": "finance_operator",
    finance_operator: "finance_operator",
    "finance-viewer": "finance_viewer",
    finance_viewer: "finance_viewer",
    auditor: "auditor",
    "support-readonly": "support-readonly",
    support_readonly: "support-readonly",
    "support-read-only": "support-readonly",
    // ALL-5181: sandbox_designer is the dev login-domain role every signed-in person receives; it is a
    // viewer baseline, never an admin. It used to alias to org-admin, which ranked first in
    // primaryRole and let canAccessAdmin admit every dev approver and signer to the admin pages.
    // It now maps to no UI role, so admin UI comes only from a real admin role (organization_admin).

  };
  return aliasMap[key] ?? null;
}

const BACKEND_PERMISSION_MAP: Record<Permission, string[]> = {
  "wallet:create": ["walletkit.wallet.create"],
  "wallet:read": ["walletkit.wallet.read"],
  "wallet:full": ["walletkit.wallet.create", "walletkit.wallet.read"],
  // Real backend wire value (Permission.TRANSFER_WRITE.wireValue() in
  // alloy-security-core/src/main/java/build/alloy/security/Permission.java,
  // gating `POST /v1/transfers/drafts/{id}/submit` via AuthScope.TRANSFERS_WRITE).
  // "walletkit.transfer.create" does not exist anywhere in the backend and
  // always evaluated false against a real GET /v1/session/effective-access
  // response, which permanently disabled the transfers-new wizard's Submit
  // button for every real session (found at G6, ALL-4735; verified live
  // against dev.alloy.build — see tests/unit/fixtures/contract-snapshots/transfers-new/).
  "transfer:create": ["transfer.write"],
  "transfer:read": ["transfer.read"],
  "transfer:approve": [],
  "transfer:reject": [],
  "transfer:escalate": [],
  "transfer:full": ["transfer.write", "transfer.read"],
  // Real backend wire values (Permission.APPROVAL_READ/APPROVE/REJECT.wireValue()
  // in alloy-security-core/src/main/java/build/alloy/security/Permission.java):
  // "walletkit.approval.*" does not exist anywhere in the backend and always
  // evaluated false against a real GET /v1/session/effective-access response
  // (ALL-4734 G6, same bug class as the user:invite fix at ALL-4725 G6).
  "approval:read": ["approval.read"],
  "approval:approve": ["approval.approve"],
  "approval:reject": ["approval.reject"],
  // No APPROVAL_HOLD/APPROVAL_ESCALATE permission exists in the backend at all
  // (no Permission enum value, no scope) — hold/escalate have no real endpoint.
  // These keys are intentionally left unmapped to a real value; any UI gated on
  // them should be treated as unbuilt, not wired to a fabricated permission.
  "approval:hold": [],
  "approval:escalate": [],
  // No backend permission or scope means "may enter the admin portal": GET
  // /v1/session/effective-access has no such grant, and the shell decides entry from the session's
  // role names instead (primaryRole/canAccessAdmin above — an organization_admin reaches
  // /admin/users, ALL-4856). "admin.portal.access" does not exist anywhere in the backend
  // (Permission.java uses the `auth.admin_*` namespace, AuthScope.java the `admin:*` one), so every
  // control gated on admin:access — Admin > Groups, Deployment, Service diagnostics, all of them
  // unbuilt backends — was already disabled for every real session. Intentionally left unmapped to
  // a real value, like approval:hold below, so it fails closed rather than claiming an authority
  // the backend cannot grant.
  "admin:access": [],
  // Real backend wire value (Permission.AUTH_BOOTSTRAP_ADMIN_INVITE.wireValue()
  // in alloy-security-core/src/main/java/build/alloy/security/Permission.java);
  // "admin.user.invite" does not exist anywhere in the backend and always
  // evaluated false against a real GET /v1/session/effective-access response
  // (ALL-4725 G6).
  "user:invite": ["auth.bootstrap_admin.invite"],
  // Real backend wire values (Permission.AUTH_ADMIN_USERS_DEACTIVATE / AUTH_ADMIN_USERS_READ
  // .wireValue() in alloy-security-core/.../Permission.java, gating POST
  // /v1/admin/users/{id}/suspend and GET /v1/admin/users). "admin.user.suspend" and
  // "admin.user.read" do not exist in the backend, so the Suspend control was disabled for every
  // real admin session (found by the first-admin capability contract, ALL-4856).
  "user:suspend": ["auth.admin_users.deactivate"],
  "user:read": ["auth.admin_users.read"],
  // No user-edit route exists: AdminUserManagementController serves POST
  // /v1/admin/users/{id}/suspend (Permission.AUTH_ADMIN_USERS_DEACTIVATE) and PATCH
  // /v1/admin/users/{id}/roles (Permission.AUTH_ROLE_MANAGE) and nothing else that mutates a user —
  // there is no PATCH /v1/admin/users/{id}. "admin.user.manage" does not exist anywhere in the
  // backend, so the handlerless "Edit User" button it gated was hidden from every real admin (and
  // did nothing when it rendered). Intentionally left unmapped, like approval:hold: the role editor
  // on /admin/users/:id, gated by role:full, is the real edit path.
  "user:full": [],
  // Real backend wire value (Permission.AUTH_ADMIN_USERS_READ.wireValue() in
  // alloy-security-core/.../Permission.java, derived from AuthScope.ADMIN_USERS_READ
  // "admin:users:read" by AuthScopePermissionMapper, gating GET /v1/admin/roles in
  // GatewayRouteScopes). "admin.role.read" does not exist in the backend, so the role catalog read
  // was gated by a key no session carries.
  "role:read": ["auth.admin_users.read"],
  // Real backend wire value (Permission.AUTH_ROLE_MANAGE.wireValue(), derived from
  // AuthScope.AUTH_ROLES_MANAGE "auth_roles:manage" by AuthScopePermissionMapper, gating PATCH
  // /v1/admin/users/{id}/roles — the only route that assigns roles, and the one the role editor on
  // /admin/users/:id calls). "admin.role.manage" does not exist in the backend and always evaluated
  // false against a real GET /v1/session/effective-access response, which hid the role editor from
  // every real admin even when the catalog loaded (ALL-4856). Deliberately the permission wire value
  // and not the scope string, as with webhook:manage below: usePermission only ever compares
  // grantedPermissions.
  "role:full": ["auth.role.manage"],
  // Real backend wire value (Permission.AUTH_ADMIN_USERS_READ.wireValue(), via
  // AuthScope.ADMIN_USERS_READ, gating the read-only GET /v1/admin/groups and
  // GET /v1/admin/groups/{groupId}/members in GatewayRouteScopes). Groups have no write route, so no
  // group key can be granted authority beyond this read. "admin.group.read" does not exist in the
  // backend.
  "group:read": ["auth.admin_users.read"],
  // No group mutation route exists — GatewayRouteScopes serves only the two group GETs above — so a
  // group "manage" affordance is unbuilt. "admin.group.manage" does not exist in the backend.
  // Intentionally left unmapped, like approval:hold: it fails closed for every session.
  "group:full": [],
  // No /v1/admin/service-accounts route exists in the gateway (AdminUserManagementController serves
  // users, invites, roles, groups, sessions, login history and audit only), and the page
  // (features/admin/service-account-governance.tsx) is a read-only fixture that calls no API. The
  // enums do carry Permission.AUTH_SERVICE_ACCOUNT_MANAGE / AuthScope.SERVICE_ACCOUNTS_MANAGE, but
  // no gateway route requires either, so mapping this key to them would enable a control the backend
  // cannot honour. "admin.service-account.manage" does not exist in the backend. Intentionally left
  // unmapped.
  "service-account:manage": [],
  // Real backend wire value (Permission.AUTH_ADMIN_AUDIT_READ.wireValue() in
  // alloy-security-core/.../Permission.java, gating GET /v1/admin/audit via
  // AdminUserManagementController.listAuditEvents). "admin.audit.read" does
  // not exist anywhere in the backend and always evaluated false against a
  // real GET /v1/session/effective-access response for a session that does
  // hold the scope, permanently hiding the audit log for every real allowed
  // session (found at G6, ALL-4729; verified against dev.alloy.build — see
  // tools/synthetic-customer-lab/fixtures/contract-snapshots/admin-audit-log/
  // session-effective-access-allowed.json, grantedPermissions).
  "audit-log:read": ["auth.admin_audit.read"],
  // No organization-configuration route exists in the gateway: AuthScope.ORGS_CREATE governs
  // POST /v1/orgs on the identity path, not an admin page control, and there is no /v1/admin/org*
  // route at all. "admin.org.configure" does not exist in the backend. Intentionally left unmapped,
  // like approval:hold.
  "org:configure": [],
  // GET /v1/admin/auth-config is not served by the gateway (acknowledged unbuilt backend in
  // tools/first-admin-gate/contract-exemptions.json -> unbacked_routes), so the auth-config page
  // edits a client-side fixture and calls no API. "admin.auth.configure" does not exist in
  // Permission.java. Intentionally left unmapped: the Save and Run controls stay disabled for every
  // session, which matches the missing route.
  "auth:configure": [],
  // Real backend wire values (Permission.CONFIG_READ / Permission.CONFIG_WRITE
  // .wireValue() in alloy-security-core/.../Permission.java, gating GET/PUT
  // /v1/config/settings and /v1/config/branding via ConfigController).
  // "admin.settings.read"/"admin.settings.manage" do not exist anywhere in the
  // backend and always evaluated false against a real GET /v1/session/effective-access
  // response, which permanently hid the Settings page's Save controls for every
  // real session (found at G1/G2, ALL-4738; verified against dev.alloy.build —
  // see tools/synthetic-customer-lab/fixtures/contract-snapshots/settings/).
  "settings:read": ["config.read"],
  "settings:full": ["config.write"],
  // Real backend wire values (Permission.NOTIFICATION_READ / .NOTIFICATION_WRITE
  // .wireValue(), gating GET/PUT /v1/notifications/preferences via NotificationController).
  "notifications-prefs:read": ["notification.read"],
  "notifications-prefs:full": ["notification.write"],
  "policy:read": ["policykit.policy.read"],
  "policy:full": ["policykit.policy.manage"],
  "monitoring:read": ["monitoring.read"],
  // Unbuilt (ALL-5219). GatewayRouteScopes has no reconciliation route at all and no route requires
  // AuthScope.RECONCILIATION_MANAGE/_EXPORT, every /reconciliation page is a G6 DeferredPage, and
  // "reconciliation.run" is not a Permission.java wire value — so this key can match no real session.
  // It fails closed rather than claiming reconflow.watch.manage, which governs watch configuration,
  // not "run reconciliation". Probe: affordance-probes.json -> reconciliation-home:run-reconciliation.
  "reconciliation:run": [],
  "evidence:read": ["evidence.read"],
  // Unbuilt (ALL-5219). The controls this key gates call no route at all: Admin > Audit Trail's
  // "Export CSV" is a handlerless button (features/admin/audit-trail.tsx) and the config viewer's
  // download buttons only toast. The gateway exposes no audit-evidence export route — the real
  // export lives on auth-service (AuthAdminRouteScopes: GET /internal/auth/evidence/export[-jobs]
  // requires AuthScope.AUDIT_EXPORT -> Permission.AUDIT_EVIDENCE_EXPORT) and is not fronted here — so
  // the contract records the gap (tools/first-admin-gate/contract-exemptions.json -> ui_keys,
  // affordance-probes.json -> admin-audit-log:export-audit). The former "evidence.export" is not a
  // Permission.java wire value and matched no session, so the control was already dead; this fails
  // closed instead of borrowing "audit.evidence.export" for a route the SPA cannot call.
  "evidence:export": [],
  "treasury:read": ["treasury.read"],
  // Unbuilt (ALL-5219). No /v1/treasury* route exists in GatewayRouteScopes, Permission.java has no
  // treasury namespace, and every /treasury page is a G6 DeferredPage — so "treasury.intent.create"
  // never matched a real session. Fails closed rather than borrowing walletkit's
  // transaction-intent permission: a treasury liquidity/staking intent is not a transaction intent,
  // so that value would gate the wrong capability. Probe: affordance-probes.json ->
  // treasury-home:create-intent.
  "treasury:create-intent": [],
  "treasury:execute": ["treasury.execute"],
  // Webhook ingestion permission; the webhook page separately checks webhooks:manage.
  "webhook:manage": ["webhook.provider.ingest"],
  // Real backend wire value (Permission.AUTH_IDENTITY_PROVIDER_MANAGE.wireValue()
  // in alloy-security-core/src/main/java/build/alloy/security/Permission.java),
  // mapped from AuthScope.IDENTITY_PROVIDERS_MANAGE ("identity_providers:manage")
  // by AuthScopePermissionMapper. The gateway serves both SSO domain reads and
  // writes on that one scope today (GatewayRouteScopes: oidcOnly/oidcStepUpRequired
  // + scopeOnly(IDENTITY_PROVIDERS_MANAGE)); SPEC-SSO-3 §1 declares a separate
  // `identity_providers:read`, which does not exist in AuthScope yet, so the page
  // gates on the manage permission and the API stays the enforcement point.
  "sso:manage": ["auth.identity_provider.manage"],
};

/**
 * The role the shell treats as the session's role when a session carries several (IdP domain role
 * plus durable assignments, sorted by the backend). The most privileged recognised role wins, so an
 * organization admin whose roleNames happen to sort a lower role first still reaches the admin
 * portal (ALL-4856). Falls back to the first name, then to "wallet_operator" as before.
 */
export function primaryRole(roleNames: readonly string[] | null | undefined): string {
  const names = Array.isArray(roleNames) ? roleNames.filter((name) => typeof name === "string" && name) : [];
  const rank = (name: string): number => {
    const role = normalizeRole(name);
    if (!role) return Number.MAX_SAFE_INTEGER;
    return ROLE_PRIORITY.indexOf(role) === -1 ? ROLE_PRIORITY.length : ROLE_PRIORITY.indexOf(role);
  };
  const ranked = [...names].sort((a, b) => rank(a) - rank(b));
  return ranked[0] ?? "wallet_operator";
}

const ROLE_PRIORITY: AppRole[] = ["org-admin", "security-admin", "wallet-admin", "compliance-officer"];

/**
 * The scope the gateway requires for POST /v1/signer/pairings (AuthScope.AUTH_ROLES_MANAGE in
 * alloy-security-core; GatewayRouteScopes.requiredRoute plus a fresh AAL2 step-up). The signer
 * "Pair a phone" button is gated on it via useSignerPairingAllowed. It is an admin governance scope
 * only: it never selects a signer binding, key or HSM token (custody stays with VaultCore and Mesh).
 */
export const SIGNER_PAIRING_SCOPE = "auth_roles:manage";

export function hasBackendPermission(grantedPermissions: string[], permission: Permission): boolean {
  const backendKeys = BACKEND_PERMISSION_MAP[permission];
  if (!backendKeys?.length) return false;
  return backendKeys.every((key) => grantedPermissions.includes(key));
}

export function hasPermission(role: string | null | undefined, permission: Permission): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  const perms = ROLE_PERMISSIONS[normalized];
  if (!perms) return false;
  return perms.includes(permission);
}

export function hasAnyPermission(role: string | null | undefined, permissions: Permission[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

export function hasAllPermissions(role: string | null | undefined, permissions: Permission[]): boolean {
  return permissions.every((p) => hasPermission(role, p));
}

/**
 * Convenience check for whether a role may enter the admin portal.
 */
export function canAccessAdmin(role: string | null | undefined): boolean {
  return hasPermission(role, "admin:access");
}

/**
 * Convenience check for whether a role may access organization settings.
 */
export function canAccessSettings(role: string | null | undefined): boolean {
  return hasPermission(role, "settings:read");
}

/**
 * Map of role display labels for the current auth context.
 */
export function roleDisplayLabel(role: string | null | undefined): string {
  if (!role) return "Unknown";
  const normalized = normalizeRole(role);
  const labels: Record<AppRole, string> = {
    "org-admin": "Organization Admin",
    "security-admin": "Security Admin",
    transaction_signer: "Transaction Signer",
    hotwallet_viewer: "Hot Wallet Viewer",
    hotwallet_cosigner_requester: "Hot Co-signer Requester",
    hotwallet_cosigner_approver: "Hot Co-signer Approver",
    executive_viewer: "Executive Viewer",
    finance_lead: "Finance Lead",
    governed_transfer_approver: "Transfer Approver",
    compliance_admin: "Compliance Administrator",
    compliance_reviewer: "Compliance Reviewer",
    developer: "Developer",
    "wallet-admin": "Wallet Administrator",
    wallet_operator: "Wallet Operator",
    "compliance-officer": "Compliance Officer",
    policy_approver: "Policy Approver",
    "treasury-operator": "Treasury Operator",
    finance_operator: "Finance Operator",
    finance_viewer: "Finance Viewer",
    auditor: "Auditor",
    "support-readonly": "Support Read-Only",
    support_readonly: "Support Read-Only",
  };
  return normalized ? labels[normalized] : role;
}

/**
 * Display labels of the roles that hold `permission`, so a permission-denied
 * surface can name the role a person needs to be granted (rather than telling
 * them only that they are denied). Ordered as the role table declares them —
 * most-privileged first — and de-duplicated, because the `support-readonly` /
 * `support_readonly` aliases share one label. Purely additive: it reads the
 * same ROLE_PERMISSIONS table `hasPermission` uses and never grants anything.
 */
export function rolesGrantingPermission(permission: Permission): string[] {
  const labels: string[] = [];
  for (const role of Object.keys(ROLE_PERMISSIONS) as AppRole[]) {
    if (ROLE_PERMISSIONS[role].includes(permission)) {
      const label = roleDisplayLabel(role);
      if (label && !labels.includes(label)) labels.push(label);
    }
  }
  return labels;
}
